import type { CompanyRow, StaffEmployeeRow } from "@/lib/staff-api";
import { toCsv, type CsvColumn } from "@/lib/csv";
import { daysSince, formatPhone } from "@/lib/formatters";
import { badgeExpiry, STATUS_LABELS } from "@/lib/roster";
import { dayString } from "@/lib/staff-payments";

export const EMPLOYEE_CSV_COLUMNS: CsvColumn[] = [
  { key: "lastName", header: "Last name" },
  { key: "firstName", header: "First name" },
  { key: "company", header: "Company" },
  { key: "contactEmail", header: "Contact email" },
  { key: "stage", header: "Stage" },
  { key: "daysSinceChange", header: "Days since last change" },
  { key: "lastChange", header: "Last change" },
  { key: "clearanceDate", header: "Clearance date" },
  { key: "badgeExpires", header: "Badge expires" },
  { key: "photoOnFile", header: "Photo on file" },
  { key: "noLongerEmployed", header: "No longer employed" },
];

export const COMPANY_CSV_COLUMNS: CsvColumn[] = [
  { key: "company", header: "Company" },
  { key: "contact", header: "Contact" },
  { key: "phone", header: "Phone" },
  { key: "email", header: "Email" },
  { key: "employees", header: "Employees" },
  { key: "waitingOnYou", header: "Waiting on you" },
  { key: "lastOrder", header: "Last order" },
  { key: "registered", header: "Registered" },
];

const yesNo = (value: boolean | undefined): string => (value ? "Yes" : "No");
const day = (iso: string): string => dayString(new Date(iso));

/** The Employees table as a spreadsheet: what the table shows, every page. */
export function employeesCsv(rows: StaffEmployeeRow[], now: Date = new Date()): string {
  return toCsv(
    rows.map((r) => ({
      lastName: r.lastName,
      firstName: r.firstName,
      company: r.vendor?.companyName ?? "Vendor not on file",
      contactEmail: r.vendor?.email ?? "",
      stage: STATUS_LABELS[r.status],
      daysSinceChange: daysSince(r.updatedAt, now),
      lastChange: day(r.updatedAt),
      clearanceDate: r.clearanceDate ?? "",
      badgeExpires: badgeExpiry(r.clearanceDate) ?? "",
      photoOnFile: yesNo(r.photoOnFile),
      noLongerEmployed: yesNo(r.inactive),
    })),
    EMPLOYEE_CSV_COLUMNS,
  );
}

/** The Companies table as a spreadsheet. */
export function companiesCsv(rows: CompanyRow[]): string {
  return toCsv(
    rows.map((r) => ({
      company: r.companyName,
      contact: r.contactName,
      phone: formatPhone(r.contactPhone),
      email: r.email,
      employees: r.employeeCount,
      waitingOnYou: r.waitingOnYou,
      lastOrder: r.lastOrderAt ? day(r.lastOrderAt) : "",
      registered: day(r.createdAt),
    })),
    COMPANY_CSV_COLUMNS,
  );
}

export function csvFilename(kind: "employees" | "companies", now: Date = new Date()): string {
  return `${kind}-${dayString(now)}.csv`;
}
